import React, { useEffect, memo, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';

import { userPropTypes } from 'helpers/propTypes';

import compraSeguraImage from 'views/Checkout/assets/compra-segura.png';

import Input from 'components/commons/Input';
import Button from 'components/commons/Button';
import Select from 'components/commons/Select';
import ShippingQuote from 'components/commons/ShippingQuote';

import './DeliveryForm.scss';
import { getProvinces } from 'services/user.services';

const validate = (values) => {
  const errors = {};
  if (!values.first_name) {
    errors.first_name = 'Nombre inválido';
  }
  if (!values.last_name) {
    errors.last_name = 'Apellido inválido';
  }
  if (!values.email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
    errors.email = 'Dirección de email inválida';
  }
  if (!values.id_card || !/^[0-9]*$/i.test(values.id_card)) {
    errors.id_card = 'Número inválido';
  }
  if (!values.phone || !/^(54)?[1-9]\d{9}$/i.test(values.phone)) {
    errors.phone = 'Número inválido (Ej: 541123456789)';
  }
  if (!values.address) {
    errors.address = 'Dirección inválida';
  }
  if (!values.postal_code) {
    errors.postal_code = 'Código postal inválido';
  }
  if (!values.locality) {
    errors.locality = 'Localidad inválida';
  }
  if (!values.province) {
    errors.province = 'Provincia inválida';
  }
  return errors;
};

const DeliveryForm = (props) => {
  const {
    handleSubmit, initialize, onSubmit, submitting, user,
    couponText, setCouponText, onSubmitCoupon, coupon, emailComplete,
    setEmailComplete, validationEmailCoupon, setEmailCoupon,
    cartQuote, cartQuoteLoading, getCartQuoteAction, zipCode,
    showShippingQuote = false,
  } = props;
  const [provinces, setProvinces] = useState([]);
  const options = provinces.map((province) => ({ label: province.name, value: province.name }));

  const callProvinces = async () => {
    try {
      const response = await getProvinces();
      setProvinces(response.items);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  useEffect(() => {
    callProvinces();
    let initialValues = {};
    if (user) {
      initialValues = { ...initialValues, ...user };
      setEmailComplete(true);
      setEmailCoupon(user.email);
    }
    if (zipCode) {
      initialValues = { ...initialValues, postal_code: zipCode };
    }
    initialize(initialValues);
    // eslint-disable-next-line
  }, [user, setEmailComplete, setEmailCoupon, zipCode]);

  const onKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmitCoupon();
    }
  };

  return (
    <div className="container checkout-step checkout-step--delivery" data-testid="delivery-form">
      <div className="row">
        <div className="form-container">
          <div className="checkout-section-header">
            <div>
              <span className="checkout-section-kicker">Paso 1</span>
              <h3 className="checkout-section-title">Datos de envío</h3>
              <div className="checkout-section-subtitle">
                Completá tus datos para calcular el envío y habilitar el pago.
              </div>
            </div>
            <div className="secure-badge" aria-label="Compra segura">
              <img className="secure-badge__image" src={compraSeguraImage} alt="Compra segura" />
              <div className="secure-badge__text">
                <div className="secure-badge__title">Compra segura</div>
                <div className="secure-badge__subtitle">Datos protegidos</div>
              </div>
            </div>
          </div>

          <form className="redux-form checkout-form-grid" onSubmit={handleSubmit(onSubmit)}>
            {showShippingQuote && (
              <div className="checkout-form-full">
                <ShippingQuote
                  onSubmit={getCartQuoteAction}
                  quote={cartQuote}
                  zipCode={zipCode}
                  loading={cartQuoteLoading}
                  showQuote={false}
                />
              </div>
            )}

            {((showShippingQuote && !cartQuoteLoading && cartQuote) || !showShippingQuote) && (
              <>
                <Field component={Input} label="Nombre" name="first_name" />
                <Field component={Input} label="Apellido" name="last_name" />
                <Field
                  component={Input}
                  disabled={!!user}
                  label="Email"
                  onChange={(event) => validationEmailCoupon(event.target.value)}
                  name="email"
                />
                <Field
                  component={Input}
                  label={process.env.REACT_APP_CLIENT_ID === '5' ? 'CUIT' : 'DNI'}
                  name="id_card"
                  type="number"
                />
                <Field component={Input} label="Celular / WhatsApp" name="phone" type="number" />
                <Field component={Input} label="Dirección" name="address" />
                <Field component={Input} label="Altura" name="number" type="number" />
                <Field component={Input} label="Piso" name="floor" />
                <Field component={Input} label="Departamento" name="departament" />
                <Field component={Input} label="Código postal" name="postal_code" />
                <Field component={Input} label="Localidad" name="locality" />
                <Field label="Provincia" name="province" component={Select} options={options} />

                <div className="checkout-coupon-card checkout-form-full">
                  <div>
                    <strong>Cupón de descuento</strong>
                    <small>Ingresá tu email para poder validar el cupón.</small>
                  </div>
                  <div className="checkout-coupon-row">
                    <input
                      aria-label="Aplicar cupón"
                      className="form-control checkout-coupon-input"
                      disabled={!!coupon || !emailComplete}
                      name="coupon"
                      onChange={(event) => setCouponText(event.target.value)}
                      onKeyPress={onKeyPress}
                      placeholder="Ingresar cupón"
                      type="text"
                      value={couponText || ''}
                    />
                    <Button
                      buttonClass={!coupon ? 'btn-search-set checkout-coupon-button' : 'btn-search-reset checkout-coupon-button'}
                      disabled={!emailComplete || submitting}
                      text={!coupon ? 'Aplicar' : <i className="fa fa-trash-o" aria-hidden="true" />}
                      type="button"
                      onClick={onSubmitCoupon}
                    />
                  </div>
                </div>

                <div className="checkout-form-full checkout-checkbox-line">
                  <Field
                    component={Input}
                    label="Deseo recibir novedades y promociones"
                    name="suscribe"
                    type="checkbox"
                  />
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

DeliveryForm.defaultProps = {
  user: null,
  coupon: null,
  couponText: '',
  emailComplete: false,
  zipCode: '',
};

DeliveryForm.propTypes = {
  coupon: PropTypes.shape({
    id: PropTypes.number.isRequired,
    percentage: PropTypes.number.isRequired,
  }),
  handleSubmit: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  user: userPropTypes,
  couponText: PropTypes.string,
  setCouponText: PropTypes.func.isRequired,
  emailComplete: PropTypes.bool,
  onSubmitCoupon: PropTypes.func.isRequired,
  setEmailComplete: PropTypes.func.isRequired,
  setEmailCoupon: PropTypes.func.isRequired,
  validationEmailCoupon: PropTypes.func.isRequired,
  cartQuote: PropTypes.object,
  cartQuoteLoading: PropTypes.bool.isRequired,
  zipCode: PropTypes.string,
  showShippingQuote: PropTypes.bool,
};

export default reduxForm({
  form: 'delivery',
  validate,
})(memo(DeliveryForm));
